import React, { useEffect, useState } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Badge, Card, CardHead, Tabs } from '../../components/ui';
import { LoadingState, EmptyState, ErrorState, SaveIndicator } from '../../features/shared/PageStates';
import { formatDate, humanize } from '../../lib/format';

export function TasksPage({ onNavigate, showToast, user }) {
  const [tab, setTab] = useState('My tasks');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [form, setForm] = useState({ title: '', due_date: '', deal_id: '' });

  const load = () => {
    setLoading(true);
    setError('');
    const req = tab === 'My tasks' ? dealMgmt.myTasks() : dealMgmt.listTasks();
    req
      .then(res => setTasks(res.tasks || res.items || []))
      .catch(e => {
        setError(e.message);
        showToast?.(e.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!form.title.trim() || busy) return;
    setBusy(true);
    setSaveStatus('saving');
    try {
      await dealMgmt.createTask({
        title: form.title,
        due_date: form.due_date || null,
        deal_id: form.deal_id || null,
        assignee_id: user?.id,
      });
      setForm({ title: '', due_date: '', deal_id: '' });
      setSaveStatus('saved');
      showToast?.('Task created');
      load();
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e) {
      setSaveStatus('error');
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleDone = async (task) => {
    setBusy(true);
    try {
      await dealMgmt.updateTask(task.id, {
        status: task.status === 'done' || task.status === 'completed' ? 'open' : 'done',
      });
      load();
    } catch (e) {
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Tabs tabs={['My tasks', 'All tasks']} active={tab} onChange={setTab} />
      <Card style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 0' }}>
          <CardHead title="Create task" />
          <SaveIndicator status={saveStatus} />
        </div>
        <div className="form-row fr3" style={{ padding: '0 16px 16px' }}>
          <input className="field-input" placeholder="Task title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          <input className="field-input" type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
          <button className="btn btn-p" disabled={busy} onClick={create}>{busy ? 'Saving…' : 'Add task'}</button>
        </div>
      </Card>

      {loading && <LoadingState label="Loading tasks…" />}
      {error && !loading && <ErrorState message={error} onRetry={load} />}
      {!loading && !error && tasks.length === 0 && (
        <EmptyState title="No tasks" sub="Create a task or assign one from a deal." />
      )}
      {!loading && !error && tasks.length > 0 && (
        <Card style={{ marginTop: 12 }}>
          <table className="tbl">
            <thead>
              <tr><th>Task</th><th>Status</th><th>Due</th><th>Deal</th><th></th></tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.id}>
                  <td className="fw">{t.title}</td>
                  <td><Badge type={t.status === 'done' || t.status === 'completed' ? 'success' : 'neutral'}>{humanize(t.status || 'open')}</Badge></td>
                  <td>{formatDate(t.due_date)}</td>
                  <td>
                    {t.deal_id ? (
                      <button className="btn btn-o btn-sm" onClick={() => onNavigate('dm-deal-detail', { dmDealId: t.deal_id })}>
                        {t.deal_ref || 'Open'}
                      </button>
                    ) : '—'}
                  </td>
                  <td>
                    <button className="btn btn-o btn-sm" disabled={busy} onClick={() => toggleDone(t)}>
                      {t.status === 'done' || t.status === 'completed' ? 'Reopen' : 'Complete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

export default TasksPage;
